import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    messageErrorConfirmDialog,
    messageSuccessConfirmDialog,
    PropsMessageDialog,
} from "../../../components/message-dialog";
import { handlePromise } from "../../../utils/handlePromisse";
import { TutorialModel } from "../../../models/TutorialModel";
import { saveTutorial, getAllTutorial, getById } from "../../../services/tutorialService";
import { formatDuration } from "../../../utils/duration";

export const TUTORIAL_CATEGORIES = ["Primeiros passos", "IA", "Pacientes", "Receitas", "Conta"];
export const CLIENT_PAGE_SIZE = 10;

const productViewInitial: TutorialModel = {
    id: undefined,
    enabled: true,
    title: "",
    subtitle: "",
    description: "",
    url: "",
    new_icon: true,
    category: "Primeiros passos",
};

const productSchema = Yup.object().shape({
    title: Yup.string().required("Este campo é obrigatório"),
    subtitle: Yup.string().required("Este campo é obrigatório"),
    description: Yup.string().required("Este campo é obrigatório"),
    url: Yup.string().required("Este campo é obrigatório"),
    new_icon: Yup.string().required("Este campo é obrigatório"),
});

/** Estado, formik, fetch e handlers da administração de tutoriais. */
export function useTutorialAdmin() {
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [tutorialViewModel, setTutorialViewModel] = React.useState<TutorialModel>(productViewInitial);
    const [durationText, setDurationText] = useState("");
    const [btnSaveDisabled, setBtnSaveDisabled] = React.useState<boolean>(false);
    const [allTutorial, setAllTutorial] = React.useState<TutorialModel[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
    const [clientPage, setClientPage] = useState(1);
    const [dialogModel, setDialogModel] = React.useState<PropsMessageDialog>({
        onConfirm: () => {
            dialogModel.open = false;
            setDialogModel({ ...dialogModel });
        },
        open: false,
    } as PropsMessageDialog);

    const formik = useFormik({
        initialValues: productViewInitial,
        validationSchema: productSchema,
        onSubmit: () => {
            registerTutorial();
        },
    });

    const fetchAllTutorial = useCallback(async () => {
        const [result, error] = await handlePromise(getAllTutorial());
        setAllTutorial(result.data as TutorialModel[]);
    }, []);

    useEffect(() => {
        fetchAllTutorial();
    }, [fetchAllTutorial]);

    const resetForm = () => {
        setTutorialViewModel({ ...productViewInitial });
        setDurationText("");
    };

    const registerTutorial = () => {
        setBtnSaveDisabled(true);
        setOpenBackdrop(true);
        const tutorial: TutorialModel = {
            ...tutorialViewModel,
        };
        saveTutorial(tutorial)
            .then(() => {
                const message = tutorial.id == null ? "Tutorial cadastrado com sucesso!" : "Tutorial alterado com sucesso";
                setDialogModel({ ...messageSuccessConfirmDialog(dialogModel.onConfirm, message) });
                resetForm();
                setOpenModal(false);
            })
            .catch(() => setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) }))
            .finally(() => {
                fetchAllTutorial();
                setOpenBackdrop(false);
                setBtnSaveDisabled(false);
            });
    };

    const updateFormikValues = (formikInstance: any, values: any) => {
        Object.entries(values).map(([key, val]) => (formikInstance.values[key] = val));
    };

    useEffect(() => {
        updateFormikValues(formik, tutorialViewModel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tutorialViewModel]);

    const editTutorial = async (tutorial: TutorialModel) => {
        setOpenBackdrop(true);
        if (tutorial.id) {
            Promise.all([getById(tutorial.id)]).then((response) => {
                if (response[0].status !== 200) {
                    setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
                    return;
                }
                tutorialViewModel.id = tutorial.id;
                tutorialViewModel.title = tutorial.title;
                tutorialViewModel.url = tutorial.url;
                tutorialViewModel.subtitle = tutorial.subtitle;
                tutorialViewModel.description = tutorial.description;
                tutorialViewModel.new_icon = tutorial.new_icon;
                tutorialViewModel.category = tutorial.category ?? "Primeiros passos";
                tutorialViewModel.durationSeconds = tutorial.durationSeconds;

                setTutorialViewModel({ ...tutorialViewModel });
                setDurationText(tutorial.durationSeconds != null ? formatDuration(tutorial.durationSeconds) : "");
                setOpenBackdrop(false);
                setOpenModal(true);
            });
        }
    };

    const handleClickEnable = useCallback(
        async (tutorial: TutorialModel) => {
            setOpenBackdrop(true);
            if (tutorial.enabled == true) {
                tutorial.enabled = false;
                await saveTutorial(tutorial);
            } else {
                tutorial.enabled = true;
                await saveTutorial(tutorial);
            }
            await fetchAllTutorial();
            setOpenBackdrop(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleOpenCreate = () => {
        resetForm();
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        resetForm();
    };

    // Client-side filtering and pagination
    const filteredTutorials = allTutorial.filter((row) => {
        const matchesSearch = row.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
            statusFilter === "all" ||
            (statusFilter === "active" && row.enabled !== false) ||
            (statusFilter === "inactive" && row.enabled === false);
        return matchesSearch && matchesStatus;
    });
    const totalFiltered = filteredTutorials.length;
    const pagedTutorials = filteredTutorials.slice((clientPage - 1) * CLIENT_PAGE_SIZE, clientPage * CLIENT_PAGE_SIZE);

    const isEditing = tutorialViewModel.id != null;

    return {
        formik,
        openBackdrop,
        openModal,
        tutorialViewModel,
        setTutorialViewModel,
        durationText,
        setDurationText,
        btnSaveDisabled,
        allTutorial,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        clientPage,
        setClientPage,
        dialogModel,
        filteredTutorials,
        totalFiltered,
        pagedTutorials,
        isEditing,
        editTutorial,
        handleClickEnable,
        handleOpenCreate,
        handleCloseModal,
        productViewInitial,
        productSchema,
    };
}
